import { api } from "@lib/client";
import { type GenericReview } from "@lib/config/types";
import {
  processUgc,
  extractUploadBlock,
  deleteUgc,
} from "@lib/image-upload.client";
import { logger } from "@lib/logger.client";
import { type OrderItemDetails } from "@lib/review";
import type { Ticket } from "@lib/ticket";
import { type ReviewValidationResponse } from "@server/routes/api/validate";
import { useMutation } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";
import { type FieldValues, FormProvider, useForm } from "react-hook-form";
import { validate } from "uuid";
import { useNavigate } from "@tanstack/react-router";
import { DeleteImageProvider } from "@/lib/context";
import { FormErrorsContext } from "./form-errors-context";

type ReviewFormProps = {
  encodedActivityId: string;
  ticket: Ticket | undefined;
  children: React.ReactNode;
  currentStep: string;
  orderItemDetails?: OrderItemDetails;
  config: GenericReview;
};

export function ReviewForm({
  encodedActivityId,
  ticket,
  children,
  currentStep,
  orderItemDetails,
  config,
}: ReviewFormProps) {
  const methods = useForm();
  const blackBoxRef = useRef<HTMLInputElement | null>(null);
  const navigate = useNavigate();

  const [validationErrors, setValidationErrors] = useState<
    Record<string, string>
  >({});
  const formValues = methods.getValues();
  const validationErrorsWithoutControl = Object.entries(
    validationErrors,
  ).filter(([key]) => !(key in formValues));

  useEffect(() => {
    Object.entries(validationErrors).forEach(([key, value]) => {
      methods.setError(key, { message: value });
    });
  }, [validationErrors]);

  const queryParams = new URLSearchParams(window.location.search);
  const product = queryParams.get("product")?.split("?")[0];

  const uploadBlock = extractUploadBlock(
    Object.values(config.pages[Number(currentStep)])[0],
  );

  const deleteImage = useCallback(
    (fileName: string) => deleteUgc(encodedActivityId, "claim", fileName),
    [encodedActivityId],
  );

  const { mutateAsync } = useMutation({
    mutationFn: async (formData: FieldValues) => {
      const blackBox = (blackBoxRef && blackBoxRef.current?.value) ?? "";

      const ugc = formData["ugc"] as File[] | undefined;
      const { ugcMeta, processedImages } = await processUgc({
        methods,
        uploadBlock,
        encodedActivityId,
        flow: "review",
        id: orderItemDetails?.id ?? "",
        ugc,
      });

      if (processedImages) {
        formData["syndicator-question:userGeneratedContent"] = processedImages;
      }

      const res = await api.validate.review.$post({
        json: {
          encodedActivityId,
          ticket,
          formData: {
            ...formData,
            ugc: ugcMeta,
            blackBox,
          },

          currentStep: currentStep,
          orderDetails: orderItemDetails,
          product: product ? validate(product) : undefined,
        },
      });
      return res.json();
    },
    onError: (error) => {
      logger.error(error, "Error handling the mutation");
    },
    onSuccess: (data: ReviewValidationResponse) => {
      if (!data.success && "errors" in data) {
        const errors: Record<string, string> = {};
        Object.entries(data.errors ?? {}).forEach(([key, value]) => {
          if (typeof value === "string") {
            errors[key] = value;
          } else if (value && typeof value === "object" && "message" in value) {
            errors[key] = JSON.stringify(value);
          }
        });
        setValidationErrors(errors);
        return;
      }

      if ("redirect" in data && data.redirect) {
        // Force a full reload, to ensure data is loaded
        void navigate({
          ...data.redirect,
          reloadDocument: true,
        });
        return;
      }

      if (data.success) {
        if (currentStep === "0") {
          methods.reset();
        }
      }
    },
  });

  async function onSubmit(formData: FieldValues) {
    try {
      await mutateAsync(formData);
    } catch (error) {
      logger.error(error, "Error handling review submission");
    }
  }

  return (
    <main>
      <DeleteImageProvider value={deleteImage}>
        <FormErrorsContext.Provider value={{ validationErrorsWithoutControl }}>
          <FormProvider {...methods}>
            <form
              onSubmit={(e) => {
                methods.clearErrors();
                void methods.handleSubmit(onSubmit)(e);
              }}
            >
              <input
                ref={blackBoxRef}
                type="hidden"
                name="blackBox"
                id="ioBlackBox"
              />
              {children}
            </form>
          </FormProvider>
        </FormErrorsContext.Provider>
      </DeleteImageProvider>
    </main>
  );
}
